/* eslint-disable max-len */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  DNABox,
  DNAButton,
  DNAText,
} from '@alucio/lux-ui';
import { StyleSheet } from 'react-native';
import {
  AISearchResultsProps,
  getSummaryParagraphs,
  useAISearchBase,
} from 'src/components/Header/SearchInput/aiSearch/useAISearchHooks';
import {
  ReferencesSection,
  SummarySection,
} from 'src/components/Header/SearchInput/aiSearch/AISearchResults';
import AISearchFeedback, {
  SearchRating,
} from 'src/components/Header/SearchInput/aiSearch/AISearchFeedback';
import { AILoadingAnimation } from '../../../components/LoadingAnimation/AILoadingAnimation';

export const PLAYGROUND_DOCUMENT_VERSION_IDS = [
  'bb45c547-e24a-45b4-9c16-05b3790a6a52_1',
  '040f7060-0286-4c74-8baa-13d060d02c7e_1',
] as const;

const PLAYGROUND_LONG_SUPPORTING_DOCUMENT_TITLE = 'Neoadjuvant osimertinib ± chemotherapy vs chemotherapy alone in resectable epidermal growth factor receptor-mutated (EGFRm) NSCLC NeoADAURA.pptx (2026-09-29, 2)';

const PLAYGROUND_LONG_SUPPORTING_DOCUMENT_EXCERPT = 'This intentionally long supporting-document excerpt verifies the card layout when a supporting source contains more content than can fit in a single line. It mirrors the multi-line description shown in the approved design, giving the preview enough text to wrap naturally while the Internal Use Only badge and document actions remain visible in their dedicated rows below the excerpt. Users can confirm that the card retains a predictable height, truncates the fourth line, and keeps every available action accessible without wrapping into the badge row or overflowing the card boundary.';

export const PLAYGROUND_AI_SEARCH_RESPONSE = {
  scientific_summary: {
    answer_found: true,
    header: {
      title: 'Scientific Summary',
      disclaimer: 'Based on scientific content in Beacon. AI responses may include mistakes. Please review before proceeding.',
      original_question: 'What does the induction of aminolevulinate synthase 1 enzyme result in?',
    },
    body_paragraphs: [
      {
        narrative_text: 'The induction of aminolevulinate synthase 1 (ALAS1) results in the accumulation of neurotoxic heme intermediates, specifically aminolevulinic acid (ALA) and porphobilinogen (PBG), in the context of acute hepatic porphyria (AHP). This accumulation occurs when ALAS1 is activated by various exogenous and endogenous triggers, including certain drugs, alcohol use, fasting, infections, stress, and hormonal fluctuations. The increased ALAS1 transcription in the liver, combined with enzyme deficiencies in the heme biosynthesis pathway, prevents the normal conversion of these intermediates to heme, leading to their pathological accumulation.',
        citations: [
          {
            document_version_id: PLAYGROUND_DOCUMENT_VERSION_IDS[0],
            source_name: PLAYGROUND_LONG_SUPPORTING_DOCUMENT_TITLE,
            page_number: '1',
            exact_quoted_text: PLAYGROUND_LONG_SUPPORTING_DOCUMENT_EXCERPT,
          },
        ],
      },
      {
        narrative_text: 'The neurotoxic accumulation of ALA and PBG resulting from ALAS1 induction causes potentially severe and life-threatening clinical manifestations affecting multiple organ systems. These include severe neurovisceral pain in the abdomen, chest, and back; neurological symptoms such as weakness, numbness, confusion, anxiety, seizures, and hallucinations; and respiratory failure in severe cases. Additionally, increased ALAS1 activity in AHP may result in elevated homocysteine plasma levels due to reduced hepatic transsulfuration activity from low heme availability and vitamin B6 depletion, as both cystathionine synthase and cystathionine lyase require heme and vitamin B6 as cofactors.',
        citations: [
          {
            document_version_id: PLAYGROUND_DOCUMENT_VERSION_IDS[0],
            source_name: PLAYGROUND_LONG_SUPPORTING_DOCUMENT_TITLE,
            page_number: '1',
            exact_quoted_text: PLAYGROUND_LONG_SUPPORTING_DOCUMENT_EXCERPT,
          },
        ],
      },
    ],
    messaging_documents: {
      included: true,
      relevance_assessment: 'The messaging documents are directly relevant to the question. The AHP/Givosiran Scientific Communications Platform contains detailed information about the disease process of acute hepatic porphyria (AHP), which is fundamentally driven by induction of the ALAS1 enzyme. The documents explain the biochemical consequences of ALAS1 induction in the context of AHP pathophysiology.',
      narrative_text: 'In acute hepatic porphyria (AHP), induction of the aminolevulinate synthase 1 (ALAS1) enzyme results in an accumulation of toxic porphyrin precursors. ALAS1 catalyzes the first and rate-limiting step in hepatic heme biosynthesis, converting succinyl CoA and glycine into 5-aminolevulinic acid (ALA). When enzyme deficiencies in AHP reduce heme synthesis, the resulting depletion of the hepatic free heme pool causes loss of negative feedback regulation on ALAS1, leading to ALAS1 induction. This induction causes overproduction and accumulation of porphyrin precursors, particularly ALA and porphobilinogen (PBG), which are toxic to multiple organ systems. The accumulation of these toxic porphyrin precursors is thought to cause the multisystemic disease manifestations of AHP, including neurological damage (primarily from ALA neurotoxicity), cutaneous lesions, hepatic complications, and kidney disease. The excess ALA is believed to cause neuronal damage through multiple mechanisms including mimicry of gamma-aminobutyric acid (GABA), oxidative stress induction, mitochondrial energetic defects, inflammation, and cell death. This pathophysiological mechanism forms the basis for targeted therapeutic approaches like givosiran, which specifically targets ALAS1 mRNA to reduce the production of ALAS1 enzyme and limit the synthesis of toxic porphyrin precursors.',
      citations: [
        {
          document_version_id: PLAYGROUND_DOCUMENT_VERSION_IDS[1],
          source_name: 'CML.pptx',
          page_number: '1',
          exact_quoted_text: 'Playground-only citation from a published tenant document.',
        },
      ],
    },
  },
};

const styles = StyleSheet.create({
  contentWrapper: {
    columnGap: 24,
    rowGap: 24,
    flexWrap: 'wrap',
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  leftSideContainer: {
    flex: 1,
    minWidth: 400,
    rowGap: 24,
  },
  rightSideContainer: {
    minWidth: 400,
    rowGap: 24,
  },
});

export const useMockAISearch = ({
  searchText,
}: Pick<AISearchResultsProps, 'searchText'>) => {
  const {
    dispatchAiSearch,
    report,
    references,
    loading,
    statusMessage,
    error,
    activeSearchText,
    userId,
    tenantId,
    fetchReferences,
  } = useAISearchBase();

  const startSearch = useCallback(async () => {
    if (!userId || !tenantId || !searchText) return;
    if (searchText === activeSearchText) return;

    dispatchAiSearch({ type: 'INITIATE_SEARCH', payload: { searchText } });
    const { handleMockSearch } = await import('./aiSearchHandlers');
    handleMockSearch({
      dispatchAiSearch,
      fetchReferences,
      mockDataOverride: {
        onLangflowResponse: {
          response: JSON.stringify(PLAYGROUND_AI_SEARCH_RESPONSE),
        },
      },
    });
  }, [
    userId,
    tenantId,
    searchText,
    activeSearchText,
    dispatchAiSearch,
    fetchReferences,
  ]);

  useEffect(() => {
    if (searchText && searchText !== activeSearchText) {
      startSearch();
    }
  }, [searchText, startSearch, activeSearchText]);

  return {
    loading,
    error,
    report,
    statusMessage,
    references,
    activeSearchText,
    startSearch,
  };
};

const AISearchResults: React.FC<AISearchResultsProps> = (props) => {
  const { searchText } = props;
  const [referenceWidth, setReferenceWidth] = useState<number | null>(null);
  const {
    loading,
    error,
    report,
    statusMessage,
    references,
    activeSearchText,
  } = useMockAISearch({ searchText });
  const [selectedFindingId, setSelectedFindingId] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<SearchRating | null>(null);

  useEffect(() => {
    setSelectedFindingId(null);
    setSelectedRating(null);
  }, [searchText]);

  const summaryParagraphs = useMemo(
    () => (report ? getSummaryParagraphs(report) : []),
    [report],
  );

  const selectedFinding = useMemo(() => {
    if (selectedFindingId === null) return null;
    return summaryParagraphs[selectedFindingId] || null;
  }, [selectedFindingId, summaryParagraphs]);

  return (
    <DNABox appearance="col" spacing="md">
      <DNABox appearance="col" spacing="sm">
        <DNAText h4>Supporting document layout — BEAC-8263</DNAText>
        <DNAText b2 status="tertiary">
          Responsive uses the available space. Choose a fixed width to check narrow cards. Use tablet mode for iPad-size buttons.
        </DNAText>
        <DNABox spacing="sm" wrap="start">
          <DNAButton
            appearance={referenceWidth === null ? 'filled' : 'outline'}
            onPress={() => setReferenceWidth(null)}
          >
            Responsive
          </DNAButton>
          {[400, 440, 600].map((width) => (
            <DNAButton
              key={width}
              appearance={referenceWidth === width ? 'filled' : 'outline'}
              onPress={() => setReferenceWidth(width)}
            >
              {`${width} px`}
            </DNAButton>
          ))}
        </DNABox>
      </DNABox>
      <DNABox style={styles.contentWrapper}>
        {/* LEFT SIDE: SUMMARY */}
        <DNABox
          appearance="col"
          spacing="lg"
          style={loading && !report ? { flex: 1, width: '100%' } : styles.leftSideContainer}
        >
          {loading && !report ? (
            <DNABox
              appearance="col"
              alignY="center"
              alignX="center"
              fill
              spacing="sm"
              style={{ justifyContent: 'center' }}
            >
              <AILoadingAnimation message={statusMessage || 'Thinking...'} />
              <DNAText b2 status="subtle" style={{ opacity: 0.8, textAlign: 'center' }}>
                You can continue using the app while the results are being generated.
              </DNAText>
            </DNABox>
          ) : (
            <>
              <SummarySection
                loading={loading}
                error={error}
                report={report}
                statusMessage={statusMessage}
                hasSubmittedSearch={activeSearchText === searchText}
                selectedFindingId={selectedFindingId}
                onFindingSelect={setSelectedFindingId}
                searchText={searchText}
                summaryParagraphs={summaryParagraphs}
              />
              {report?.scientific_summary && (
                <AISearchFeedback
                  selectedRating={selectedRating}
                  onRate={setSelectedRating}
                />
              )}
            </>
          )}
        </DNABox>
        {/* RIGHT SIDE: REFERENCES */}
        {(!loading || report) && (
          <DNABox
            appearance="col"
            spacing="lg"
            style={[
              styles.rightSideContainer,
              referenceWidth === null ? { flex: 1 } : { width: referenceWidth },
            ]}
          >
            <ReferencesSection
              loading={loading}
              report={report}
              references={references}
              selectedFinding={selectedFinding}
              onClearFilter={() => setSelectedFindingId(null)}
            />
          </DNABox>
        )}
      </DNABox>
    </DNABox>
  );
};

export const PlaygroundAiSearch = () => {
  return (
    <AISearchResults
      searchText="What does the induction of aminolevulinate synthase 1 enzyme result in?"
    />
  );
};
